import { useContext, useMemo } from "react";
import ToastContext from "../context/ToastContext";
import { getDefaultDuration } from "../utils/toast/toastHelpers";

/**
 * Custom hook for accessing toast notification methods
 * Provides a convenient API for triggering different types of toasts
 *
 * @throws {Error} If used outside of ToastProvider
 * @returns {object} Toast methods (success, error, warning, info, loading, dismiss)
 *
 * @example
 * const toast = useToast();
 * toast.success("Operation successful!");
 * toast.error("Something went wrong", { duration: 5000 });
 * const loadingId = toast.loading("Processing...");
 * toast.dismiss(loadingId);
 */
const useToast = () => {
  const context = useContext(ToastContext);

  // Error handling for usage outside ToastProvider
  if (context === undefined) {
    throw new Error(
      "useToast must be used within a ToastProvider. " +
        "Please wrap your component tree with <ToastProvider>."
    );
  }

  const { addToast, removeToast } = context;

  // Memoize toast methods to prevent unnecessary re-renders
  const toast = useMemo(() => {
    /**
     * Create a toast with the specified type
     * @private
     */
    const createToast = (type, message, options = {}) => {
      const {
        duration = getDefaultDuration(type),
        dismissible = true,
        position = "top-center",
        action = null,
      } = options;

      return addToast({
        type,
        message,
        duration,
        dismissible,
        position,
        action,
      });
    };

    return {
      /**
       * Display a success toast notification
       * @param {string} message - The message to display
       * @param {object} options - Optional configuration
       * @param {number} options.duration - Auto-dismiss duration in ms (default: 3000)
       * @param {boolean} options.dismissible - Whether toast can be manually dismissed (default: true)
       * @param {string} options.position - Position on screen (default: 'top-center')
       * @returns {string} Toast ID
       */
      success: (message, options) => createToast("success", message, options),

      /**
       * Display an error toast notification
       * @param {string} message - The message to display
       * @param {object} options - Optional configuration
       * @param {number} options.duration - Auto-dismiss duration in ms (default: 5000)
       * @param {boolean} options.dismissible - Whether toast can be manually dismissed (default: true)
       * @param {string} options.position - Position on screen (default: 'top-center')
       * @param {object} options.action - Optional action button {label: string, onClick: function}
       * @returns {string} Toast ID
       */
      error: (message, options) => createToast("error", message, options),

      /**
       * Display a warning toast notification
       * @param {string} message - The message to display
       * @param {object} options - Optional configuration
       * @param {number} options.duration - Auto-dismiss duration in ms (default: 4000)
       * @param {boolean} options.dismissible - Whether toast can be manually dismissed (default: true)
       * @param {string} options.position - Position on screen (default: 'top-center')
       * @returns {string} Toast ID
       */
      warning: (message, options) => createToast("warning", message, options),

      /**
       * Display an info toast notification
       * @param {string} message - The message to display
       * @param {object} options - Optional configuration
       * @param {number} options.duration - Auto-dismiss duration in ms (default: 4000)
       * @param {boolean} options.dismissible - Whether toast can be manually dismissed (default: true)
       * @param {string} options.position - Position on screen (default: 'top-center')
       * @returns {string} Toast ID
       */
      info: (message, options) => createToast("info", message, options),

      /**
       * Display a loading toast notification
       * Loading toasts do not auto-dismiss and must be manually dismissed
       * @param {string} message - The message to display
       * @param {object} options - Optional configuration
       * @param {number} options.duration - Auto-dismiss duration in ms (default: 0 - no auto-dismiss)
       * @param {boolean} options.dismissible - Whether toast can be manually dismissed (default: true)
       * @param {string} options.position - Position on screen (default: 'top-center')
       * @returns {string} Toast ID for later dismissal
       */
      loading: (message, options) => createToast("loading", message, options),

      /**
       * Dismiss a specific toast by ID
       * @param {string} id - The ID of the toast to dismiss
       */
      dismiss: (id) => removeToast(id),
    };
  }, [addToast, removeToast]);

  return toast;
};

export default useToast;
export { useToast };
